import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SaveIcon from '@mui/icons-material/Save';
import Menu from '@/components/Menu';
import Button from '@/trio-ui/Button';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import { InterviewTypes } from '@/utils/interviews';
import * as Styles from './InterviewBox.styles';
import { CHALLENGE_TECHNOLOGIES_DROPDOWN } from '../ChallengeTechnologiesDropdown/ChallengeTechnologiesDropdown.constants';
import IconButton from '../IconButton';
import { SectionBoxType } from '../SectionBox/SectionBox.component';
import SubmitTechnicalChallengeModal from '../SubmitTechnicalChallengeModal';

type CandidateType = {
  name: string;
  email: string;
  isEditingEmail: boolean;
  isEmailValid: boolean;
  draft: string;
  id: string;
};

export type InterviewBoxType = Pick<
  SectionBoxType,
  'isCollapsible' | 'isOpen' | 'onToggleOpen' | 'autoScroll' | 'onAutoScroll'
> & {
  candidate: CandidateType;
  isSubmitButtonDisabled: boolean;
  isAlreadySent: boolean;
  hideCandidateDetails: boolean;
  hideSubmitButton: boolean;
  type: InterviewTypes;
  onSubmitInterview: (interviewId: string, type: InterviewTypes) => void;
  onEditCandidateEmail: () => void;
  onChangeCandidateEmail: (event: any) => void;
  onDeleteInterview: () => void;
  onEditInterview: () => void;
  interviewName: string;
  technicalChallengeName?: string;
  technicalChallengeId?: string;
  onCancelEditingEmail: () => void;
  onSaveCandidateEmail: () => void;
  id: string;
  children: JSX.Element;
  interviewLink?: string;
  isSent?: boolean;
  onRefetchInterview: () => void;
  onCreateInterview: (id: string, onCreateSuccess?: () => void) => void;
  technologyUsedInChallenge?: string; // Deprecated: kept for backward compatibility
  skillName?: string;
};

export default function InterviewBox({
  interviewLink,
  interviewName,
  type,
  hideCandidateDetails,
  hideSubmitButton,
  candidate,
  onSubmitInterview,
  onEditCandidateEmail,
  isSubmitButtonDisabled,
  isAlreadySent,
  onChangeCandidateEmail,
  onEditInterview,
  onDeleteInterview,
  onCancelEditingEmail,
  onSaveCandidateEmail,
  id,
  children,
  technicalChallengeName,
  technicalChallengeId,
  isSent = false,
  onRefetchInterview,
  onCreateInterview,
  technologyUsedInChallenge, // Deprecated: kept for backward compatibility
  skillName,
  ...sectionBoxProps
}: InterviewBoxType): JSX.Element {
  const isTechnicalChallengeType = type === InterviewTypes.TECHNICAL_CHALLENGE;
  const [isSubmitTechnicalChallengeModalOpen, setIsSubmitTechnicalChallengeModalOpen] =
    useState<boolean>(false);

  const shouldDisplayFooter = isTechnicalChallengeType ? !technicalChallengeName : !isAlreadySent;
  const shouldDisplayLink = Boolean(
    isTechnicalChallengeType ? technicalChallengeName : isAlreadySent && interviewLink
  );

  // Helper function to determine technology type
  const getTechnologyType = (technology: string): string => {
    return (
      CHALLENGE_TECHNOLOGIES_DROPDOWN.find((tech) => tech.label === technology)?.technologyType ??
      'other'
    );
  };

  return (
    <Styles.CustomSectionBox
      title={interviewName}
      id={id}
      leftElement={
        isTechnicalChallengeType ? (
          <>
            <Styles.ChallengeTag $isFirst $technologyType="other">
              {technicalChallengeName || 'New Challenge'}
            </Styles.ChallengeTag>
            {(skillName || technologyUsedInChallenge) && (
              <Styles.ChallengeTag
                $isFirst={false}
                $technologyType={getTechnologyType(skillName || technologyUsedInChallenge || '')}
              >
                {skillName || technologyUsedInChallenge}
              </Styles.ChallengeTag>
            )}
          </>
        ) : (
          <></>
        )
      }
      rightElement={
        <Menu
          icon={MoreHorizIcon}
          menuItems={[
            {
              label: 'Edit',
              menuItemIcon: CreateIcon,
              callback: () => onEditInterview(),
            },
            {
              label: 'Delete',
              menuItemOptions: { warning: true },
              menuItemIcon: DeleteIcon,
              callback: (event) => {
                event.stopPropagation();
                onDeleteInterview();
              },
            },
          ]}
        />
      }
      {...sectionBoxProps}
    >
      <div>
        {!hideCandidateDetails && (
          <Styles.CandidateDetails>
            <div className="flex">
              <Styles.InterviewInfo>
                <div className="flex">
                  <Styles.SendTo>{isSent ? 'Sent to' : 'Sending to'}</Styles.SendTo>
                  <Styles.CandidateName>{candidate.name}</Styles.CandidateName>
                </div>

                {!candidate.isEditingEmail ? (
                  <div className="flex align-center">
                    <Styles.CandidateEmail>
                      <EmailIcon sx={{ width: 14, mr: '6px' }} />
                      {candidate.email}
                    </Styles.CandidateEmail>
                    {!isAlreadySent && (
                      <IconButton onClick={onEditCandidateEmail} icon={CreateIcon} />
                    )}
                  </div>
                ) : (
                  <div className="flex align-center">
                    <Styles.EmailInput>
                      <InputText
                        errorMessage={!candidate.isEmailValid ? 'Enter a valid e-mail.' : ''}
                        name="candidate-email"
                        label=""
                        onChangeHandler={onChangeCandidateEmail}
                        value={candidate.email}
                      />
                    </Styles.EmailInput>
                    <TransparentButton
                      disabled={!candidate.isEmailValid}
                      onClick={onSaveCandidateEmail}
                      isBlueButton
                    >
                      <>
                        <SaveIcon />
                        Save
                      </>
                    </TransparentButton>
                    {!!candidate.draft && (
                      <TransparentButton onClick={onCancelEditingEmail} isRedButton>
                        <>
                          <CloseIcon />
                          Cancel
                        </>
                      </TransparentButton>
                    )}
                  </div>
                )}
              </Styles.InterviewInfo>

              {shouldDisplayLink && (
                <Styles.InterviewInfo withBorder>
                  <Styles.SendTo>
                    {isTechnicalChallengeType ? 'Challenge Name & Link' : 'Interview Link'}
                  </Styles.SendTo>

                  <Styles.LinkBox>
                    {isTechnicalChallengeType && (
                      <Styles.ChallengeName>{technicalChallengeName}</Styles.ChallengeName>
                    )}

                    {interviewLink && (
                      <Styles.InterviewLink target="_blank" href={interviewLink} rel="noreferrer">
                        <LinkIcon fontSize="small" />
                        {interviewLink}
                      </Styles.InterviewLink>
                    )}
                  </Styles.LinkBox>
                  <div className="flex spacing-4">
                    {isTechnicalChallengeType && (
                      <div style={{ marginTop: 16 }}>
                        <Button onClick={() => setIsSubmitTechnicalChallengeModalOpen(true)}>
                          Add manually
                        </Button>
                      </div>
                    )}
                    {/* TODO: Add post send */}
                    {/* {isTechnicalChallengeType && !isAlreadySent && (
                      <div style={{ marginTop: 16 }}>
                        <Button
                          disabled={isSubmitButtonDisabled}
                          onClick={() => onSubmitInterview(id)}
                        >
                          Send
                        </Button>
                      </div>
                    )} */}
                  </div>
                </Styles.InterviewInfo>
              )}
            </div>
          </Styles.CandidateDetails>
        )}

        {children}

        {!hideSubmitButton && shouldDisplayFooter && (
          <Styles.Footer className="flex spacing-4">
            {isTechnicalChallengeType && (
              <Button
                disabled={isSubmitButtonDisabled}
                onClick={() =>
                  onCreateInterview(id, () => setIsSubmitTechnicalChallengeModalOpen(true))
                }
                variantStyle="outline"
                size="medium"
                style={{ maxWidth: 'unset', width: 'auto' }}
              >
                Create without sending
              </Button>
            )}
            <Button
              disabled={isSubmitButtonDisabled}
              onClick={() => onSubmitInterview(id, type)}
              variantStyle="filled"
              size="medium"
            >
              Send to candidate
            </Button>
          </Styles.Footer>
        )}
      </div>
      {isTechnicalChallengeType && (
        <SubmitTechnicalChallengeModal
          interviewId={id}
          technicalChallengeId={technicalChallengeId || ''}
          isOpen={isSubmitTechnicalChallengeModalOpen}
          onClose={() => setIsSubmitTechnicalChallengeModalOpen(false)}
          onSuccess={onRefetchInterview}
        />
      )}
    </Styles.CustomSectionBox>
  );
}
